import {
  type AiProposalInput,
  type AiProposalRecord,
  buildAiProposalRecord,
} from '~/models/aiProposal'
import type { ValidationIssues } from '~/models/common'
import { toIsoTimestamp } from '~/types/dates'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { SolaDatabase } from '../db'
import { type StorageError, storageError, toStorageError } from '../errors'
import { notifyWritten } from '../invalidation'
import { STORE } from '../schema'

/**
 * Stored second opinions (D-094 §5).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  ADD AND READ. THERE IS NO UPDATE AND NO SINGLE-RECORD DELETE
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  `schema.ts` says a proposal is immutable once written, and this file is where
 *  that stops being a comment. **Do not add an update function**: an edited second
 *  opinion is a second opinion nobody gave, and the whole value of keeping them is
 *  being able to compare two readings that were each true when made.
 *
 *  Deleting is deliberately all-or-nothing and lives in
 *  `services/selectiveDeletionService.ts` as its own category, decided rather than
 *  discovered (D-094 §5). A per-record delete would let a user quietly remove the
 *  one disagreement in a run of agreements, which makes the history a curated
 *  argument rather than a record.
 *
 *  **This repository holds no product rule.** Whether a proposal may be requested
 *  at all — the token, the meter, the network, the preview — is
 *  `services/aiReviewService.ts`. This only writes what already came back clean.
 */

/**
 * The history, newest first.
 *
 * Read **through the `requestedAt` index** rather than sorted after a `getAll`,
 * because the index is the store's stated reason for existing. A caller that
 * sorted in memory would keep working if the index were dropped, and the drop
 * would go unnoticed until the history grew.
 */
export async function listAiProposals(
  db: SolaDatabase,
): Promise<Result<readonly AiProposalRecord[], StorageError>> {
  try {
    const ascending = await db.getAllFromIndex(STORE.aiProposals, 'requestedAt')
    return ok([...ascending].reverse())
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Store one proposal.
 *
 * **`add`, not `put`.** A duplicate id is a real error worth surfacing rather
 * than an overwrite that silently loses a previous opinion — and since the id is
 * generated here and never derived, a collision means something is wrong that a
 * successful write would hide.
 */
export async function addAiProposal(
  db: SolaDatabase,
  input: AiProposalInput,
  now: Date,
): Promise<Result<AiProposalRecord, StorageError>> {
  try {
    const built = buildAiProposalRecord(input, toIsoTimestamp(now))
    if (!built.ok) return err(invalid(built.error))

    await db.add(STORE.aiProposals, built.value)
    notifyWritten(STORE.aiProposals)
    return ok(built.value)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

function invalid(issues: ValidationIssues): StorageError {
  return storageError('invalidRecord', issues.map((entry) => entry.message).join(' '), issues)
}
